// ============================================================
// KeyDetectButton
// ============================================================
// A small button that runs key detection on a set of chords
// and shows a popover with ranked candidates to choose from.
//
// Used in two places:
//   - Toolbar (song-level detection)
//   - SectionBlock header (section-level detection)
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Chord, Key } from '../../core/types';
import { detectKey, type KeyCandidate } from '../../core/keyDetection';
import { formatKey } from '../../core/formatter';

// ------------------------------------------------------------
// CONFIDENCE BAR
// ------------------------------------------------------------

function ConfidenceBar({ score }: { score: number }) {
  return (
    <div style={{
      width: 40,
      height: 3,
      background: 'var(--color-rule)',
      borderRadius: 2,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      <div style={{
        width: `${Math.round(score * 100)}%`,
        height: '100%',
        background: 'var(--color-accent)',
        borderRadius: 2,
        transition: 'width 200ms ease',
      }} />
    </div>
  );
}

// ------------------------------------------------------------
// PROPS
// ------------------------------------------------------------

interface KeyDetectButtonProps {
  /** All chords to analyse */
  chords: Chord[];
  /** The current effective key (used to resolve chord pitch classes) */
  currentKey: Key;
  /** Called when the user selects a candidate key */
  onSelectKey: (key: Key) => void;
  /** Label shown on the button */
  label?: string;
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

export function KeyDetectButton({
  chords,
  currentKey,
  onSelectKey,
  label = '?',
}: KeyDetectButtonProps) {
  const [open, setCandidates]     = useState<KeyCandidate[] | null>(null);
  const [loading, setLoading]     = useState(false);
  const ref                       = useRef<HTMLDivElement>(null);
  const hasChords                 = chords.length > 0;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setCandidates(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleDetect = useCallback(() => {
    if (!hasChords) return;
    setLoading(true);
    // Run synchronously but wrap in setTimeout to let loading state render
    setTimeout(() => {
      const candidates = detectKey(chords, currentKey, 4);
      setCandidates(candidates);
      setLoading(false);
    }, 0);
  }, [chords, currentKey]);

  const handleSelect = useCallback((key: Key) => {
    onSelectKey(key);
    setCandidates(null);
  }, [onSelectKey]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleDetect}
        disabled={!hasChords}
        title={hasChords ? 'Detect key from chords' : 'Add chords first'}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          padding: '2px 6px',
          borderRadius: 999,
          border: '1px solid var(--color-rule)',
          background: 'transparent',
          color: hasChords ? 'var(--color-ink-secondary)' : 'var(--color-ink-muted)',
          cursor: hasChords ? 'pointer' : 'not-allowed',
          opacity: hasChords ? 1 : 0.4,
          transition: 'all 120ms',
          letterSpacing: '0.04em',
          lineHeight: 1.4,
        }}
        className={hasChords ? 'hover:border-accent hover:text-accent' : ''}
      >
        {loading ? '…' : label}
      </button>

      {/* Candidates popover */}
      {open && open.length > 0 && (
        <div
          className="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            padding: '8px 4px',
            minWidth: 140,
            zIndex: 200,
          }}
        >
          <div style={{
            fontSize: 9,
            fontFamily: 'var(--font-label)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--color-ink-muted)',
            padding: '0 8px 6px',
          }}>
            Detected keys
          </div>

          {open.map((candidate, i) => (
            <button
              key={`${candidate.key.root}-${candidate.key.scale}`}
              onClick={() => handleSelect(candidate.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '5px 8px',
                background: i === 0 ? 'rgba(var(--color-accent-rgb), 0.06)' : 'transparent',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'background 100ms',
                textAlign: 'left',
              }}
              className="hover:bg-hover"
            >
              {/* Key label */}
              <span style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 15,
                color: i === 0 ? 'var(--color-accent)' : 'var(--color-ink-primary)',
                lineHeight: 1,
                minWidth: 32,
              }}>
                {candidate.label}
              </span>

              {/* Confidence bar */}
              <ConfidenceBar score={candidate.score} />

              {/* Percentage */}
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                color: 'var(--color-ink-muted)',
                minWidth: 28,
                textAlign: 'right',
              }}>
                {Math.round(candidate.score * 100)}%
              </span>
            </button>
          ))}

          <div style={{
            borderTop: '1px solid var(--color-rule)',
            margin: '6px 8px 2px',
            paddingTop: 6,
          }}>
            <span style={{
              fontSize: 9,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-ink-muted)',
            }}>
              Click a key to apply it
            </span>
          </div>
        </div>
      )}

      {open && open.length === 0 && (
        <div className="dropdown" style={{
          position: 'absolute', top: '100%', left: 0,
          marginTop: 4, padding: '10px 12px', zIndex: 200,
        }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-ink-muted)' }}>
            No clear key detected
          </span>
        </div>
      )}
    </div>
  );
}
